import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./Wireframes.css";

export default function Wireframes() {
    const services = [
        {
            image: "assets/img/GameDevelopment/wireframes/ui.jpg",
            title: "Game UX Wireframes",
            description: "Plan game screens, player decisions, onboarding, lobby flow, gameplay states, store paths, and reward loops before visual design begins.",
            points: ["Screen structure", "Player actions", "Game states"]
        },
        {
            image: "assets/img/GameDevelopment/wireframes/multiplayer.png",
            title: "Multiplayer Flow Wireframes",
            description: "Map matchmaking, game rooms, private tables, ready states, team setup, turn flows, invites, reconnection, and result screens.",
            points: ["Room flows", "Matchmaking", "Result states"]
        },
        {
            image: "assets/img/GameDevelopment/wireframes/store.png",
            title: "Wallet & Economy Wireframes",
            description: "Plan wallet, coins, gems, purchase flows, offers, rewards, inventory, store navigation, transaction history, and error states.",
            points: ["Wallet UX", "Store paths", "Reward flows"]
        },
        {
            image: "assets/img/GameDevelopment/wireframes/liveops.png",
            title: "LiveOps Screen Planning",
            description: "Wireframe daily rewards, missions, events, campaigns, battle passes, banners, notifications, and seasonal content screens.",
            points: ["Event flow", "Missions", "Live content"]
        }
    ];

    const wireframeFlows = [
        { image: "assets/img/GameDevelopment/SMI-Featured-App.jpg", title: "Onboarding Flow", description: "Guest mode, sign-up, permissions, tutorial steps, first reward, and first-session engagement." },
        { image: "assets/img/GameDevelopment/Best-Ludo-Game.png", title: "Game Mode Flow", description: "Mode selection, room type, entry rules, player count, difficulty, and invite options." },
        { image: "assets/img/GameDevelopment/card-game1.png", title: "Table Flow", description: "Seat selection, turn state, betting actions, card interaction, timer handling, and round results." },
        { image: "assets/img/GameDevelopment/fantasy-cricket-app-img.png", title: "Contest Flow", description: "Contest list, team builder, wallet checks, entry confirmation, leaderboard, and result flow." },
        { image: "assets/img/GameDevelopment/quizappfeat.png", title: "Quiz Flow", description: "Question state, answer selection, feedback, timer, lifelines, scoring, and progression flow." },
        { image: "assets/img/GameDevelopment/Sports-Booking-App-India.png", title: "Leaderboard Flow", description: "Ranking filters, score state, season tabs, rewards, profile view, and share actions." }
    ];

    const deliverables = [
        "User Flows",
        "Low-Fidelity Wireframes",
        "Screen Map",
        "Gameplay State Map",
        "Wallet Flow",
        "Store Flow",
        "Lobby Flow",
        "Reward Flow",
        "Edge Cases",
        "Clickable Prototype",
        "UX Notes",
        "Developer Handoff"
    ];

    const process = [
        { step: "01", title: "Game Flow Discovery", description: "We understand game genre, player types, session length, monetization, backend rules, and business goals." },
        { step: "02", title: "Screen Mapping", description: "We define required screens, navigation, player actions, entry points, game states, and exit paths." },
        { step: "03", title: "Wireframe Design", description: "We create low-fidelity layouts for gameplay, lobby, wallet, store, onboarding, rewards, and LiveOps screens." },
        { step: "04", title: "Prototype Review", description: "We connect key screens into a clickable prototype to validate logic, flow, and player experience before UI design." },
        { step: "05", title: "Handoff", description: "We deliver screen maps, wireframes, notes, edge states, and UX guidance for UI designers and developers." }
    ];

    const benefits = [
        { icon: "fa-solid fa-route", title: "Clear Player Journeys", description: "Wireframes make player actions, screen paths, and game states clear before visual design or development starts." },
        { icon: "fa-solid fa-gamepad", title: "Gameplay-Aware UX", description: "We plan interfaces around game rules, player motivation, session flow, rewards, and competition." },
        { icon: "fa-solid fa-coins", title: "Monetization Flow Planning", description: "Wallet, store, purchase, reward, ad, and offer flows are mapped early to reduce product gaps." },
        { icon: "fa-solid fa-clipboard-check", title: "Lower Rework Risk", description: "Early wireframes help catch unclear navigation, missing states, and weak flows before visual production." }
    ];

    const gallery = [
        { image: "assets/img/GameDevelopment/Battle-Royale-Game-th.png", title: "Action Game Flow" },
        { image: "assets/img/GameDevelopment/Board-Game-Banner.png", title: "Board Game Flow" },
        { image: "assets/img/GameDevelopment/onlinr-casino.jpg", title: "Casino Game Flow" },
        { image: "assets/img/GameDevelopment/endless-runner-game-thumbnail.png", title: "Casual Game Flow" },
        { image: "assets/img/GameDevelopment/fantasy-football-app-img.png", title: "Fantasy App Flow" },
        { image: "assets/img/GameDevelopment/ludowings-img.jpg", title: "Ludo App Flow" },
        { image: "assets/img/GameDevelopment/SCREEN4.png", title: "Gameplay State Flow" },
        { image: "assets/img/GameDevelopment/Creative-UI-Character-Design.png", title: "Visual Planning" }
    ];

    const faqs = [
        {
            question: "What are game wireframes?",
            answer: "Game wireframes are low-fidelity screen plans that define gameplay screens, navigation, player actions, state changes, rewards, stores, lobbies, and key user flows before visual UI design."
        },
        {
            question: "Why should a game project start with wireframes?",
            answer: "Wireframes reduce rework by clarifying screen structure, UX logic, monetization paths, game states, and development requirements before high-fidelity design starts."
        },
        {
            question: "Can you wireframe multiplayer and wallet flows?",
            answer: "Yes. We wireframe matchmaking, rooms, private tables, game states, wallets, purchases, rewards, transactions, leaderboards, and result screens."
        },
        {
            question: "Do you provide clickable wireframe prototypes?",
            answer: "Yes. We can connect important screens into clickable prototypes so teams can review flows before visual design and development."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Game Wireframe Design Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Wireframe Design",
        "areaServed": "Worldwide",
        "description": "Wireframe design services for games, gaming apps, player flows, HUDs, lobbies, wallets, rewards, stores, LiveOps, and clickable prototypes.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Game Wireframe Design Services | Game UX Flows & Prototypes | Oengines</title>
                <meta name="description" content="Oengines Games LLP creates wireframes for games and gaming apps, including player flows, HUDs, lobbies, wallets, rewards, stores, LiveOps, clickable prototypes, and UX handoff." />
                <meta name="keywords" content="game wireframe design, game UX wireframes, mobile game wireframes, game prototype design, gaming app UX, game user flow, wireframe design services" />
                <link rel="canonical" href="https://www.oengines.com/game-wireframes" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="wireframe-page">
                <section className="wireframe-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="wireframe-eyebrow">Game Wireframes & UX Flows</span>
                                <h1>Plan Game Screens, Player Flows & Monetization Before UI Design</h1>
                                <p>Oengines Games LLP creates wireframes for games and gaming apps, including gameplay screens, lobbies, wallets, rewards, stores, LiveOps flows, clickable prototypes, and UX handoff documentation.</p>
                                <div className="wireframe-actions">
                                    <a href="/contact" className="as-btn">Start Wireframes <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="wireframe-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="wireframe-hero-stats">
                                    <div><strong>Flow</strong><span>Player Journey</span></div>
                                    <div><strong>State</strong><span>Game Logic</span></div>
                                    <div><strong>UX</strong><span>Prototype Ready</span></div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="wireframe-hero-gallery">
                                    <img className="wireframe-main-shot" src="assets/img/GameDevelopment/quiztopmobile.png" alt="Game wireframe planning" />
                                    <div className="wireframe-mini-grid">
                                        <img src="assets/img/GameDevelopment/Best-Ludo-Game.png" alt="Board game wireframe" />
                                        <img src="assets/img/GameDevelopment/rummy-game-5.png" alt="Wallet wireframe" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="wireframe-service-section space">
                    <div className="container">
                        <div className="wireframe-heading text-center">
                            <span>Wireframe Services</span>
                            <h2>Game Wireframe Services for Clear Product Planning</h2>
                            <p>Wireframes help your team validate user flow, screen logic, game states, monetization paths, and technical requirements before detailed UI starts.</p>
                        </div>
                        <div className="row g-4">
                            {services.map((service) => (
                                <div className="col-lg-3 col-md-6" key={service.title}>
                                    <article className="wireframe-card">
                                        <img src={service.image} alt={service.title} />
                                        <div>
                                            <h3>{service.title}</h3>
                                            <p>{service.description}</p>
                                            <ul>
                                                {service.points.map((point) => <li key={point}>{point}</li>)}
                                            </ul>
                                        </div>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="wireframe-flow-section space">
                    <div className="container">
                        <div className="wireframe-heading text-center">
                            <span>Flow Coverage</span>
                            <h2>Game UX Flows We Plan Before UI Production</h2>
                            <p>We map the important player journeys so screens connect logically across gameplay, economy, competition, and LiveOps.</p>
                        </div>
                        <div className="wireframe-flow-grid">
                            {wireframeFlows.map((item) => (
                                <article className="wireframe-flow-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="wireframe-gallery-section space">
                    <div className="container">
                        <div className="wireframe-heading text-center">
                            <span>Visual Planning</span>
                            <h2>Image-Led Planning References for Game Wireframes</h2>
                        </div>
                        <div className="wireframe-gallery-grid">
                            {gallery.map((item) => (
                                <figure className="wireframe-gallery-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <figcaption>{item.title}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="wireframe-assets-section space">
                    <div className="container">
                        <div className="wireframe-assets-panel">
                            <div className="wireframe-heading mb-0">
                                <span>Deliverables</span>
                                <h2>Wireframe Outputs for Designers, Developers & Product Owners</h2>
                                <p>We deliver structured wireframe assets that make game UI production, backend planning, and development handoff clearer.</p>
                            </div>
                            <div className="wireframe-assets-list">
                                {deliverables.map((asset) => <span key={asset}>{asset}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="wireframe-process-section space">
                    <div className="container">
                        <div className="wireframe-heading text-center">
                            <span>Wireframe Process</span>
                            <h2>From Game Idea to Clickable UX Prototype</h2>
                        </div>
                        <div className="wireframe-process-grid">
                            {process.map((item) => (
                                <article className="wireframe-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="wireframe-benefit-section space">
                    <div className="container">
                        <div className="wireframe-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Game Wireframes Built Around Product, Economy & Player Flow</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="wireframe-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="wireframe-faq-section space">
                    <div className="container">
                        <div className="wireframe-heading text-center">
                            <span>FAQ</span>
                            <h2>Game Wireframe Questions</h2>
                        </div>
                        <div className="wireframe-faq-grid">
                            {faqs.map((item) => (
                                <article className="wireframe-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="wireframe-cta">
                    <div className="container">
                        <div className="wireframe-cta-box">
                            <div>
                                <span>Plan Before Production</span>
                                <h2>Map your game screens and player flows with Oengines.</h2>
                                <p>Share your game concept, monetization flow, target players, and core screens. We will help structure the UX before visual UI and development.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Wireframe Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
